/** @jsxRuntime classic */
/** @jsx jsx */
/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  Button,
  useDisclosure,
  useToast,
  Collapse,
  Progress,
  Box,
  Container,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Player, BottomUI, FullUI, PlayerStates } from './player.component';
import Schemas, { Constraints } from '../api/schemas';
import { lordWallet } from '..';
import Constants from '../constants';
import TrackUpload from './track.upload.component';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createAssociatedTokenAccount,
  MINT_ADDRESS,
  SendTransaction,
} from '../api/qdot';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { firebaseFacade } from '../api/firebase';
import Home from './home.component';
import { log } from '../utils';
import { isMobile, isTablet } from 'react-device-detect';
import BottomNav from './bottomenu.component';
import { configContext } from '../api/config';
import { QueuePage } from './queue.page';
import { MusicqMenu } from './navdrawer.component';
import MenuIcon from '@mui/icons-material/Menu';
import { Search } from './search';
import SearchIcon from '@mui/icons-material/Search';
import { css, jsx } from '@emotion/react';
import { getAccountInfo } from '../api/qdot';

function shuffleArray(array) {
  for (let cursor = array.length - 1; cursor > 0; cursor--) {
    const target = Math.floor(Math.random() * (cursor + 1));
    [array[cursor], array[target]] = [array[target], array[cursor]];
  }
}

export function computeTotalCost(track) {
  const [LOOPS] = Constraints.pricingUnit;
  const { track_schema } = Schemas;
  const cost = track[track_schema.price];

  const total =
    cost *
    (track[track_schema.pricing_unit] === LOOPS
      ? 1
      : track[track_schema.duration]);
  return total;
}

export const ViewId = {
  home: 8,
  bottomPlayer: 4,
  fullPlayer: 1,
  queuePage: 2,
  search: 16,
};

export const UiStates = {
  one: 1, // Player
  two: 2, // QueuePage
  six: 6, // BottomPlayer and QueuePage
  eight: 8, // Home
  twelve: 12, // Home and BottomPlayer
  sixteen: 16, // Search
};

export class NavigationEvent {
  #uiState;
  #data;
  constructor(uiState, data) {
    this.#data = data;
    this.#uiState = uiState;
  }

  get state() {
    return this.#uiState;
  }

  get data() {
    return { ...this.#data };
  }
}

function ViewSwitcher(props) {
  const {
    isHome,
    activatePlayer,
    showBottomPlayer,
    showQueuePage,
    showPlayer,
    isSearch,
  } = props;

  log({ component: 'ViewSwitcher', msg: 'ViewSwitcher props', props: props });

  const renderPlayer = () => {
    if (activatePlayer) {
      return (
        <>
          <Collapse in={showPlayer} unmountOnExit>
            <Player
              {...props}
              PlayerUIDelegate={FullUI}
              isActive={!showBottomPlayer}
            />
          </Collapse>
          <Collapse in={showBottomPlayer} unmountOnExit>
            <Player
              {...props}
              PlayerUIDelegate={BottomUI}
              bottomNav={
                (isMobile || isTablet) &&
                configContext
                  .fetchValue(configContext.configKeys.bottomNavEnable)
                  .asBoolean() && <BottomNav />
              }
              isActive={showBottomPlayer}
            />
          </Collapse>
        </>
      );
    }
  };

  const renderData = () => {
    return (
      <>
        <Collapse in={showQueuePage} unmountOnExit>
          <QueuePage {...props} />
        </Collapse>
        <Collapse in={isHome} unmountOnExit>
          <Home {...props} />
        </Collapse>
        <Collapse in={isSearch} unmountOnExit>
          <Search {...props} />
        </Collapse>
      </>
    );
  };

  return (
    <>
      {renderData()}
      {renderPlayer()}
    </>
  );
}

export default function Stream(props) {
  const { associatedTokenAddress } = props;
  const [tracks, setTracks] = useState([]);
  const [playerState, setPlayerState] = useState(PlayerStates.idle);

  const [pos, setPos] = useState();
  const [streamingq, setStreamingq] = useState(Constants.empty);
  const [openq, setOpenq] = useState(null);
  const { queue_address } = useParams();

  const { publicKey, signTransaction } = useWallet();
  const toast = useToast();

  const [loops, setLoops] = useState(0);
  const { connection } = useConnection();
  const [subscribed, setSubscribed] = useState(true);

  const [paid, setPaid] = useState(false);
  const [looping, setLooping] = useState(false);
  const audioRef = useRef();

  const [isPlayerActive, setPlayerActive] = useState(false);
  const [streamingqAddress, setStreamingqAddress] = useState(null);
  const [showBottomPlayer, setShowBottomPlayer] = useState(false);

  const [showPlayer, setShowPlayer] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showQueuePage, setShowQueuePage] = useState(false);
  
  const [home, setHome] = useState(true);
  const {
    isOpen: openMenu,
    onOpen: onOpenMenu,
    onClose: onCloseMenu,
  } = useDisclosure();

  const [viewStack, __] = useState([new NavigationEvent(UiStates.twelve, {})]);
  const [trackAddressMap, setTrackTrackAddressMap] = useState({});
  const routeNavigator = useNavigate();

  const getNext = useCallback(() => {
    if (tracks.length === 0) return 0;
    let newPos = pos + 1;
    setPaid(false);

    return newPos % tracks.length;
  }, [tracks, pos]);

  const getPrev = useCallback(() => {
    if (tracks.length === 0) return 0;
    let newPos = pos;
    if (newPos <= 0) newPos = tracks.length;

    setPaid(false);
    return --newPos;
  }, [tracks, pos]);

  const onNext = useCallback(() => {
    const newPos = getNext();
    const nextTrack = tracks[newPos] || Constants.empty;
    if (tracks[pos] === nextTrack) setLoops(loops => loops + 1);

    setPos(newPos);
  }, [tracks, pos]);

  const onPrev = useCallback(() => {
    const newPos = getPrev();
    const prevTrack = tracks[newPos] || Constants.empty;
    if (tracks[pos] === prevTrack) setLoops(loops => loops + 1);

    setPos(newPos);
  }, [tracks, pos]);

  const onPay = useCallback(setPaid, []);

  const onAudioError = useCallback(() => {
    setPlayerState(PlayerStates.paused);
  }, []);

  const deleteTrack = track => {
    log({
      component: 'Stream',
      msg: 'Deleting track',
      track: track,
    });

    if (
      !track.owner_wallet_address ||
      (publicKey && publicKey.equals(lordWallet))
    )
      firebaseFacade.deleteTrack(track);
    else if (!publicKey || publicKey.toBase58() !== track.owner_wallet_address)
      toast({
        title: 'Unauthorized Action',
        description: 'You do not have authority over this track!',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    else firebaseFacade.deleteTrack(track);
  };

  const onShuffle = () => {
    shuffleArray(tracks);
  };

  const onStateChange = state => {
    setPlayerState(state);
  };

  const onPlayToggle = useCallback(
    (queueAddress, trackAddress, isPlaying, trackAddressMap) => {
      const pos = trackAddress ? trackAddressMap[trackAddress] : 0;
      setPos(pos);
      setPlayerState(isPlaying ? PlayerStates.playing : PlayerStates.paused);

      setPlayerActive(true);
      setStreamingqAddress(queueAddress);

      log({
        component: 'Stream',
        msg: 'onPlayToggle called',
        pos: pos,
        isPlaying: isPlaying,
        queueAddress: queueAddress,
        trackAddress: trackAddress,
        trackAddressMap: trackAddressMap,
        playerState: isPlaying ? PlayerStates.playing : PlayerStates.paused,
      });
    },
    [trackAddressMap]
  );

  const handleStream = useCallback(
    (queueAddress, newQueue, isStreaming, isSame, tracks, trackAddress) => {
      const trackAddressMap = handleSetTracks(tracks);
      setPlayerActive(true);

      setStreamingq(newQueue);
      setShowBottomPlayer(true);

      if (isSame) {
        log({
          component: 'Stream',
          msg: 'handleStream called with same queue',
          queueAddress: queueAddress,
          newQueue: newQueue,
          isStreaming: isStreaming,
          tracks: tracks,
          pos: pos,
          trackAddressMap: trackAddressMap,
          trackAddress: trackAddress,
        });
        onPlayToggle(
          queueAddress,
          trackAddress || tracks[pos].track_address,
          isStreaming,
          trackAddressMap
        );
      } else {
        log({
          component: 'Stream',
          msg: 'handleStream called with different queue',
          queueAddress: queueAddress,
          newQueue: newQueue,
          isStreaming: isStreaming,
          tracks: tracks,
          pos: pos,
          trackAddressMap: trackAddressMap,
          trackAddress: trackAddress,
        });
        onPlayToggle(
          queueAddress,
          trackAddress,
          tracks.length > 0,
          trackAddressMap
        );
      }
    },
    [pos, trackAddressMap]
  );

  const handleCreateAccount = useCallback(async () => {
    const verdict = associatedTokenAddress
      ? await createAssociatedTokenAccount(
          connection,
          new PublicKey(associatedTokenAddress),
          publicKey,
          MINT_ADDRESS,
          publicKey,
          signTransaction
        )
      : false;

    if (verdict) {
      const description = `Your QDOT account has been created! Visit DexLab/Raydium to purchase some QDOTS! `;
      toast({
        title: 'Account Creation Succeeded',
        description: description,
        duration: 5000,
        isClosable: true,
        status: 'success',
        position: 'top',
      });
    } else {
      const description = `Unable to create an account. Please try again or use your preferred wallet app to add the Mint`;
      toast({
        title: 'Account Creation Failed',
        description: description,
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    }
  }, [publicKey]);

  const handleQueuePageOpen = useCallback(
    ({ previousQueueAddress, queue, tracks }) => {
      setOpenq(queue);
      log({
        component: 'Stream',
        msg: 'handleQueuePageOpen called',
        params: {
          previousQueueAddress: previousQueueAddress,
          queue: queue,
          tracks: tracks,
        },
      });
    },
    []
  );

  const handleSetTracks = useCallback(tracks => {
    const trackMap = {};
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      trackMap[track.track_address] = i;
    }

    setTracks(tracks);
    setTrackTrackAddressMap(trackMap);
    return trackMap;
  }, []);

  function render() {
    log({ component: 'Stream', msg: 'render called', viewStack: viewStack });
    const navigationEvent = viewStack[viewStack.length - 1];
    const state = navigationEvent.state;

    const data = navigationEvent.data;
    setHome(state & ViewId.home);
    setShowQueuePage(state & ViewId.queuePage);

    setShowBottomPlayer(state & ViewId.bottomPlayer);
    setShowPlayer(state & ViewId.fullPlayer);
    setShowSearch(state & ViewId.search);

    if ((state & ViewId.queuePage) > 0) {
      handleQueuePageOpen(data);
    }
  }

  const pushView = useCallback(viewState => {
    viewStack.push(viewState);
    render();
  }, []);

  const popView = useCallback(() => {
    viewStack.pop();
    render();
  }, []);

  const getAudio = useCallback(() => {
    log({
      component: 'Stream',
      msg: 'rendering audio tag',
      track: tracks[pos],
    });
    return (
      <audio
        ref={audioRef}
        src={(tracks[pos] || {}).track_uri}
        loop={looping}
      ></audio>
    );
  }, [tracks[pos], looping, loops]);

  const onSearch = useCallback(() => {
    pushView(new NavigationEvent(UiStates.sixteen, {}));
  });

  useEffect(() => {
    return firebaseFacade.loadQueue(queue => {
      pushView(
        new NavigationEvent(UiStates.six, {
          queue: queue,
          queueAddress: queue.queue_address,
        })
      );
    }, queue_address);
  }, [queue_address]);

  useEffect(() => {
    if (publicKey) {
      firebaseFacade.isSubscriber(publicKey, streamingq).then(setSubscribed);
    } else {
      setSubscribed(false);
    }
  }, [streamingqAddress, publicKey, queue_address]);

  useEffect(() => {
    audioRef.current.onerror = audioRef.current.onabort = onAudioError;
    audioRef.current.volume = 0.3;
  }, []);

  return (
    <>
      {home && (
        <Box
          position="sticky"
          top={0}
          left={0}
          right={0}
          zIndex={10}
          shadow={'lg'}
          bg={'#1A202C'}
        >
          <Container maxW={['100%', '100%', '75%', '50%', '50%']}>
            <Box
              css={css`
                display: grid;
                grid-template-columns: 50px auto 50px;
                justify-items: center;
              `}
            >
              <Image
                m={'8px'}
                height={10}
                width={10}
                src={`${process.env.PUBLIC_URL}/apple-touch-icon.png`}
                onClick={() => routeNavigator('/')}
                cursor={'pointer'}
              />
              {configContext
                .fetchValue(configContext.configKeys.searchEnabled)
                .asBoolean() ? (
                <InputGroup color={'white'} margin={'8px'}>
                  <InputLeftElement
                    pointerEvents={'none'}
                    children={<SearchIcon />}
                  />
                  <Input
                    readOnly
                    placeholder="Search for queues"
                    onClick={onSearch}
                  />
                </InputGroup>
              ) : (
                <Box />
              )}
              <Box
                margin={'8px'}
                backgroundColor={'transparent'}
                color={'yellow.400'}
                aria-label={'Menu'}
                cursor={'pointer'}
                onClick={onOpenMenu}
              >
                <MenuIcon />
              </Box>
            </Box>
            <MusicqMenu
              onAccountCreate={handleCreateAccount}
              onClose={onCloseMenu}
              isOpen={openMenu}
            />
          </Container>
        </Box>
      )}
      <Box
        style={{
          minHeight: '100%',
        }}
      >
        {getAudio()}
        <ViewSwitcher
          {...props}
          streamingq={streamingq}
          tracks={tracks}
          currentTrackAddress={(tracks[pos] || {}).track_address}
          queueArtUri={streamingq.art_uri}
          isPlaying={playerState == PlayerStates.playing}
          loops={loops}
          track={tracks[pos] || {}}
          onPlayToggle={onPlayToggle}
          onNext={onNext}
          onPrev={onPrev}
          onShuffle={onShuffle}
          onStateChange={onStateChange}
          subscribed={subscribed}
          audioRef={audioRef}
          setLooping={setLooping}
          looping={looping}
          onPay={onPay}
          paid={paid}
          onDeleteTrack={deleteTrack}
          isHome={home && queue_address === undefined}
          onStream={handleStream}
          activatePlayer={isPlayerActive}
          streamingqAddress={streamingqAddress}
          showBottomPlayer={showBottomPlayer}
          showQueuePage={showQueuePage}
          showPlayer={showPlayer}
          onPushView={pushView}
          onPopView={popView}
          isSearch={showSearch}
          openq={openq}
          trackAddressMap={trackAddressMap}
        />
      </Box>
    </>
  );
}
